import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Check, Pencil, Trash2, Clock, AlertTriangle, Lock,
  Repeat, Link2, Layers, CalendarClock, History, X, User,
} from 'lucide-react';
import { T, ROLES, springs } from '../../lib/tokens';
import { RoleBadge } from '../primitives/Badge';
import { AssigneePicker, Avatar } from './AssigneePicker';
import { supabase } from '../../lib/supabase';
import { apiFetch } from '../../lib/api';

const DURATION_OPTIONS = [
  { value: 15, label: '15m' }, { value: 30, label: '30m' },
  { value: 60, label: '1h' }, { value: 120, label: '2h' },
  { value: 240, label: '4h' }, { value: 480, label: '8h' },
];

const fmtDuration = (min) => {
  if (!min) return '';
  if (min >= 60) return `${Math.floor(min / 60)}h${min % 60 ? ` ${min % 60}m` : ''}`;
  return `${min}m`;
};

export const TaskRow = ({ task, compact, onToggle, showEta, allTasks }) => {
  const [done, setDone] = useState(task.done);
  const [editing, setEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(task.title);
  const [editPriority, setEditPriority] = useState(task.priority || 2);
  const [editEffort, setEditEffort] = useState(task.effort || '');
  const [editDuration, setEditDuration] = useState(task.duration_minutes || 30);
  const [editDeadline, setEditDeadline] = useState(task.deadline ? new Date(task.deadline).toISOString().split('T')[0] : '');
  const [editHardDeadline, setEditHardDeadline] = useState(task.hard_deadline || false);
  const [editChunking, setEditChunking] = useState(task.allow_chunking || false);
  const [editMinChunk, setEditMinChunk] = useState(task.min_chunk_minutes || 30);
  const [editMaxChunksDay, setEditMaxChunksDay] = useState(task.max_chunks_day ?? 3);
  const [editEarliestStart, setEditEarliestStart] = useState(
    task.earliest_start ? new Date(task.earliest_start).toISOString().slice(0, 16) : ''
  );
  const [editBlockers, setEditBlockers] = useState((task.blocker_ids || []).join(', '));
  const [editAssignee, setEditAssignee] = useState(task.assignee_id || null);
  const [confirmDel, setConfirmDel] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyEntries, setHistoryEntries] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);

  const toggle = async () => {
    const newDone = !done;
    setDone(newDone);
    const now = new Date().toISOString();
    await supabase.from('tasks').update({
      done: newDone,
      done_at: newDone ? now : null,
      status: newDone ? 'done' : 'todo',
    }).eq('id', task.id);

    // Auto-log time entry when completing a task
    if (newDone && task.duration_minutes) {
      await supabase.from('time_entries').insert({
        task_id: task.id,
        project_id: task.goal_id || null,
        role_id: task.role_id || null,
        date: new Date().toISOString().split('T')[0],
        duration_minutes: task.duration_minutes,
        title: task.title,
      });
    }
    // Remove time entry when un-completing
    if (!newDone) {
      await supabase.from('time_entries').delete().eq('task_id', task.id);
    }

    onToggle && onToggle();
  };

  const saveEdit = async () => {
    const blockerIds = editBlockers
      .split(',')
      .map(s => s.trim())
      .filter(s => s.length > 10); // UUID-like length check

    await supabase.from('tasks').update({
      title: editTitle,
      priority: editPriority,
      effort: editEffort || null,
      duration_minutes: editDuration,
      deadline: editDeadline ? new Date(editDeadline + 'T17:00:00Z').toISOString() : null,
      hard_deadline: editHardDeadline,
      allow_chunking: editChunking,
      min_chunk_minutes: editMinChunk,
      max_chunks_day: editChunking ? editMaxChunksDay : null,
      earliest_start: editEarliestStart ? new Date(editEarliestStart).toISOString() : null,
      blocker_ids: blockerIds.length > 0 ? blockerIds : [],
      assignee_id: editAssignee,
    }).eq('id', task.id);
    setEditing(false);
    onToggle && onToggle();
  };

  const openHistory = async () => {
    setHistoryOpen(true);
    if (historyEntries !== null) return;
    setHistoryLoading(true);
    try {
      const res = await apiFetch(`/scheduler/runs?task_id=${task.id}&limit=10`);
      const json = await res.json();
      setHistoryEntries(json.runs || []);
    } catch (e) {
      setHistoryEntries([]);
    }
    setHistoryLoading(false);
  };

  const deleteTask = async () => {
    await supabase.from('tasks').delete().eq('id', task.id);
    onToggle && onToggle();
  };

  const role = task.roles || task.goals?.roles;
  const isBlocked = task.status === 'blocked';
  const isOverdue = task.deadline && new Date(task.deadline) < new Date() && !done;
  const isAtRisk = (task.schedule_status === 'at_risk')
    || (task.eta && task.deadline && new Date(task.eta) > new Date(task.deadline));

  // Map the scheduler's per-task verdict to a small badge. blocked_by_dependency
  // is already covered by `isBlocked` above; skip it here.
  const unschedBadge = (() => {
    if (task.schedule_status !== 'unschedulable') return null;
    const reason = task.schedule_reason;
    if (!reason || reason === 'blocked_by_dependency') return null;
    const RED = (label) => ({ label, color: T.red, bg: T.redLight });
    const AMB = (label) => ({ label, color: T.amber, bg: T.amberLight });
    switch (reason) {
      case 'asap_blocked':            return RED('ASAP blocking');
      case 'evicted_no_slot':         return AMB('evicted');
      case 'no_slot_in_window':       return AMB('no slot');
      case 'no_slot_before_deadline': return AMB('no slot before deadline');
      case 'deadline_passed':         return RED('deadline passed');
      case 'window_invalid':          return AMB('invalid window');
      case 'invalid_duration':        return AMB('invalid duration');
      case 'unresolvable_dependency': return RED('unmet dep');
      default:                        return AMB(reason);
    }
  })();

  // ─── Edit Mode ──────────────────────────────────────────────────────────
  if (editing && !compact) {
    return (
      <div style={{
        padding: '14px 16px', borderRadius: 10,
        background: 'rgba(255,255,255,0.85)', border: '1px solid rgba(255,255,255,0.90)',
        boxShadow: T.shadowSm,
      }}>
        <input value={editTitle} onChange={e => setEditTitle(e.target.value)} style={{
          width: '100%', padding: '6px 10px', borderRadius: 6, fontSize: 13, border: '1px solid rgba(0,0,0,0.10)',
          background: 'rgba(255,255,255,0.8)', fontFamily: 'inherit', color: T.text, outline: 'none', marginBottom: 10,
        }} autoFocus onKeyDown={e => { if (e.key === 'Enter') saveEdit(); }} />

        {/* Row 1: Priority + Duration */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8, flexWrap: 'wrap', rowGap: 8 }}>
          <div style={{ display: 'flex', gap: 3 }}>
            {[{ v: 1, l: 'P1', c: T.red }, { v: 2, l: 'P2', c: T.amber }, { v: 3, l: 'P3', c: T.textMuted }].map(p => (
              <button key={p.v} onClick={() => setEditPriority(p.v)} style={{
                padding: '3px 8px', borderRadius: 5, fontSize: 10, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                background: editPriority === p.v ? (p.v === 1 ? T.redLight : p.v === 2 ? T.amberLight : 'rgba(0,0,0,0.04)') : 'rgba(0,0,0,0.02)',
                color: editPriority === p.v ? p.c : T.textLight, border: `1px solid ${editPriority === p.v ? 'rgba(0,0,0,0.10)' : 'rgba(0,0,0,0.04)'}`,
              }}>{p.l}</button>
            ))}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <Clock size={11} color={T.textMuted} />
            <select value={editDuration} onChange={e => setEditDuration(Number(e.target.value))} style={{
              border: '1px solid rgba(0,0,0,0.08)', borderRadius: 5, padding: '3px 6px',
              fontSize: 11, color: T.textMid, fontFamily: 'inherit', background: '#fff',
            }}>
              {DURATION_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <CalendarClock size={11} color={T.textMuted} />
            <input type="date" value={editDeadline} onChange={e => setEditDeadline(e.target.value)} title="Deadline" style={{
              border: '1px solid rgba(0,0,0,0.08)', borderRadius: 5, padding: '3px 6px',
              fontSize: 11, color: T.textMid, fontFamily: 'inherit', background: '#fff',
            }} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }} title="Earliest start">
            <span style={{ fontSize: 10, color: T.textMuted }}>From</span>
            <input type="datetime-local" value={editEarliestStart} onChange={e => setEditEarliestStart(e.target.value)} style={{
              border: '1px solid rgba(0,0,0,0.08)', borderRadius: 5, padding: '3px 6px',
              fontSize: 11, color: T.textMid, fontFamily: 'inherit', background: '#fff',
            }} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }} title="Assignee">
            <User size={11} color={T.textMuted} />
            <AssigneePicker value={editAssignee} current={task.assignee} onChange={setEditAssignee} />
          </div>
        </div>

        {/* Row 2: Toggles */}
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 8, flexWrap: 'wrap' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: T.textMid, cursor: 'pointer' }}>
            <input type="checkbox" checked={editHardDeadline} onChange={e => setEditHardDeadline(e.target.checked)} />
            Hard deadline
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: T.textMid, cursor: 'pointer' }}>
            <input type="checkbox" checked={editChunking} onChange={e => setEditChunking(e.target.checked)} />
            Allow chunking
          </label>
          {editChunking && (
            <>
              <select value={editMinChunk} onChange={e => setEditMinChunk(Number(e.target.value))} title="Smallest chunk size" style={{
                border: '1px solid rgba(0,0,0,0.08)', borderRadius: 5, padding: '2px 4px',
                fontSize: 10, color: T.textMid, fontFamily: 'inherit', background: '#fff',
              }}>
                <option value={15}>Min 15m</option>
                <option value={30}>Min 30m</option>
                <option value={60}>Min 1h</option>
                <option value={120}>Min 2h</option>
              </select>
              <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: T.textMid }} title="Max chunks per day">
                <span style={{ fontSize: 10, color: T.textMuted }}>≤</span>
                <input type="number" min={1} max={10} value={editMaxChunksDay}
                  onChange={e => setEditMaxChunksDay(Math.max(1, Math.min(10, Number(e.target.value) || 1)))}
                  style={{
                    width: 36, border: '1px solid rgba(0,0,0,0.08)', borderRadius: 5, padding: '2px 4px',
                    fontSize: 10, color: T.textMid, fontFamily: 'inherit', background: '#fff',
                  }}
                />
                <span style={{ fontSize: 10, color: T.textMuted }}>chunks/day</span>
              </label>
            </>
          )}
        </div>

        {/* Row 3: Actions */}
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <div style={{ flex: 1 }} />
          <button onClick={() => setEditing(false)} style={{ padding: '3px 10px', borderRadius: 5, border: '1px solid rgba(0,0,0,0.08)', background: 'rgba(0,0,0,0.03)', cursor: 'pointer', fontSize: 11, fontFamily: 'inherit', color: T.textMid }}>Cancel</button>
          <button onClick={saveEdit} style={{ padding: '3px 10px', borderRadius: 5, border: 'none', background: T.amber, color: '#fff', cursor: 'pointer', fontSize: 11, fontWeight: 600, fontFamily: 'inherit' }}>Save</button>
        </div>
      </div>
    );
  }

  // ─── Display Mode ───────────────────────────────────────────────────────
  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: done ? 0.45 : 1, y: 0 }}
      transition={springs.gentle}
      style={{
        display: 'flex', alignItems: 'flex-start', gap: 10,
        padding: compact ? '8px 10px' : '12px 16px',
        borderRadius: 10,
        background: isBlocked ? 'rgba(224,88,88,0.04)' : done ? 'transparent' : 'rgba(255,255,255,0.60)',
        border: `1px solid ${isBlocked ? 'rgba(224,88,88,0.15)' : done ? 'transparent' : 'rgba(255,255,255,0.85)'}`,
      }}
    >
      <button onClick={toggle} style={{
        width: 18, height: 18, borderRadius: 5, flexShrink: 0,
        border: `1.5px solid ${done ? T.green : isBlocked ? T.red : 'rgba(0,0,0,0.18)'}`,
        background: done ? T.greenLight : 'transparent',
        cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
        marginTop: 1, transition: 'all 0.15s ease',
      }}>
        {done && <Check size={11} color={T.green} />}
      </button>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{
            fontSize: compact ? 13 : 14, fontWeight: 500, color: T.text,
            textDecoration: done ? 'line-through' : 'none', lineHeight: 1.3, flex: 1,
          }}>
            {task.title}
          </span>
        </div>
        {!compact && (
          <div style={{ display: 'flex', gap: 6, marginTop: 5, flexWrap: 'wrap', alignItems: 'center' }}>
            {role?.slug && <RoleBadge roleSlug={role.slug} small />}
            {task.duration_minutes && (
              <span style={{ fontSize: 10, color: T.textMuted, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Clock size={9} /> {fmtDuration(task.duration_minutes)}
              </span>
            )}
            {task.allow_chunking && (
              <span style={{ fontSize: 9, fontWeight: 600, color: T.violet, padding: '1px 4px', borderRadius: 3, background: T.violetLight }}>
                <Layers size={8} style={{ marginRight: 2, verticalAlign: 'middle' }} />chunked
              </span>
            )}
            {task.is_recurring && (
              <Repeat size={10} color={T.blue} />
            )}
            {task.is_locked && (
              <Lock size={10} color={T.amber} />
            )}
            {isBlocked && (
              <span style={{ fontSize: 9, fontWeight: 600, color: T.red, padding: '1px 5px', borderRadius: 3, background: T.redLight }}>
                <Link2 size={8} style={{ marginRight: 2, verticalAlign: 'middle' }} />blocked
              </span>
            )}
            {isOverdue && !isBlocked && (
              <span style={{ fontSize: 9, fontWeight: 600, color: T.red, padding: '1px 5px', borderRadius: 3, background: T.redLight }}>
                overdue
              </span>
            )}
            {isAtRisk && !isOverdue && !isBlocked && (
              <span title={task.schedule_reason || 'at_risk'} style={{ fontSize: 9, fontWeight: 600, color: T.amber, padding: '1px 5px', borderRadius: 3, background: T.amberLight }}>
                <AlertTriangle size={8} style={{ marginRight: 2, verticalAlign: 'middle' }} />at risk
              </span>
            )}
            {unschedBadge && !isBlocked && (
              <span title={task.schedule_reason || ''} style={{ fontSize: 9, fontWeight: 600, color: unschedBadge.color, padding: '1px 5px', borderRadius: 3, background: unschedBadge.bg }}>
                <AlertTriangle size={8} style={{ marginRight: 2, verticalAlign: 'middle' }} />{unschedBadge.label}
              </span>
            )}
            {task.due_label && !isOverdue && (
              <span style={{ fontSize: 11, color: task.due_label === 'Today' ? T.red : T.textMuted }}>{task.due_label}</span>
            )}
            {task.deadline && !task.due_label && (
              <span style={{ fontSize: 10, color: isOverdue ? T.red : T.textMuted }}>
                {new Date(task.deadline).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
              </span>
            )}
            {task.hard_deadline && (
              <span style={{ fontSize: 9, fontWeight: 700, color: T.red }}>HARD</span>
            )}
            {showEta && task.eta && !isOverdue && (
              <span style={{ fontSize: 10, fontWeight: 500, color: T.blue, padding: '1px 5px', borderRadius: 4, background: T.blueLight }}>
                ETA {new Date(task.eta).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
              </span>
            )}
          </div>
        )}
      </div>
      {task.assignee && (
        <div style={{ flexShrink: 0, marginTop: 1 }}>
          <Avatar profile={task.assignee} size={compact ? 18 : 20} />
        </div>
      )}
      {task.priority === 1 && !done && (
        <div style={{ width: 6, height: 6, borderRadius: '50%', background: T.red, flexShrink: 0, marginTop: 6 }} />
      )}
      {!compact && (
        <div style={{ display: 'flex', gap: 2, flexShrink: 0, marginTop: 1, position: 'relative' }}>
          <button onClick={openHistory} title="Schedule history" style={{ padding: 4, border: 'none', background: 'none', cursor: 'pointer' }}>
            <History size={11} color={T.textLight} />
          </button>
          <button onClick={() => setEditing(true)} style={{ padding: 4, border: 'none', background: 'none', cursor: 'pointer' }}>
            <Pencil size={11} color={T.textLight} />
          </button>
          {confirmDel ? (
            <div style={{ display: 'flex', gap: 3, alignItems: 'center' }}>
              <button onClick={deleteTask} style={{ padding: '2px 6px', borderRadius: 4, border: 'none', background: T.red, color: '#fff', cursor: 'pointer', fontSize: 9, fontWeight: 600, fontFamily: 'inherit' }}>Del</button>
              <button onClick={() => setConfirmDel(false)} style={{ padding: '2px 6px', borderRadius: 4, border: '1px solid rgba(0,0,0,0.08)', background: 'rgba(0,0,0,0.03)', cursor: 'pointer', fontSize: 9, fontFamily: 'inherit', color: T.textMid }}>No</button>
            </div>
          ) : (
            <button onClick={() => setConfirmDel(true)} style={{ padding: 4, border: 'none', background: 'none', cursor: 'pointer' }}>
              <Trash2 size={11} color={T.textLight} />
            </button>
          )}
          <AnimatePresence>
            {historyOpen && (
              <motion.div
                initial={{ opacity: 0, y: -4, scale: 0.96 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -4, scale: 0.96 }}
                transition={springs.gentle}
                style={{
                  position: 'absolute', top: '100%', right: 0, marginTop: 6,
                  width: 320, maxHeight: 360, overflowY: 'auto',
                  background: '#fff', borderRadius: 10, boxShadow: T.shadowLg,
                  border: '1px solid rgba(0,0,0,0.08)', zIndex: 50, padding: 12,
                }}
                onClick={e => e.stopPropagation()}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: T.text }}>Schedule history</div>
                  <button onClick={() => setHistoryOpen(false)} style={{ padding: 2, border: 'none', background: 'none', cursor: 'pointer' }}>
                    <X size={12} color={T.textMuted} />
                  </button>
                </div>
                {historyLoading && (
                  <div style={{ fontSize: 11, color: T.textMuted }}>Loading…</div>
                )}
                {!historyLoading && historyEntries && historyEntries.length === 0 && (
                  <div style={{ fontSize: 11, color: T.textMuted }}>No schedule changes recorded for this task yet.</div>
                )}
                {!historyLoading && historyEntries && historyEntries.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {historyEntries.map(run => {
                      const entry = run.diff_summary[0];
                      const when = new Date(run.triggered_at).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
                      const fmt = iso => iso ? new Date(iso).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—';
                      const colour = entry.change === 'added' ? T.green : entry.change === 'removed' ? T.red : T.amber;
                      return (
                        <div key={run.id} style={{ borderLeft: `2px solid ${colour}`, paddingLeft: 8 }}>
                          <div style={{ fontSize: 10, color: T.textMuted }}>
                            {when} · {run.trigger_reason || 'unknown'}
                          </div>
                          <div style={{ fontSize: 11, color: T.text, marginTop: 2 }}>
                            {entry.change === 'added' && <>Scheduled at <strong>{fmt(entry.to?.start)}</strong></>}
                            {entry.change === 'removed' && <>Removed from <strong>{fmt(entry.from?.start)}</strong></>}
                            {entry.change === 'moved' && <>Moved <strong>{fmt(entry.from?.start)}</strong> → <strong>{fmt(entry.to?.start)}</strong></>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}
    </motion.div>
  );
};
